import React from 'react';
import styledImport from 'styled-components';
import type { ThemedStyledInterface } from 'styled-components';

import { Breakpoints, Grid, Type } from '../../../app/StyleGuide';
import type { NavbarTheme } from '../state/Navbar.theme';

const styled = styledImport as ThemedStyledInterface<NavbarTheme>;
import windowService from '../../../services/windowService';
import { limitText } from '../../../utils/textUtils';
import { useNavbarTheme } from '../state/NavbarThemeContext';

const NAVBAR_HEIGHT = '60px';

const OrgName = styled.p<{ $keepOrgNameVisible?: boolean }>`
  display: block;
  margin: 0 0 0 ${Grid._3};
  color: ${({ theme }) => theme.textColor};
  text-transform: ${({ theme }) => theme.navTextTransform};
  letter-spacing: ${({ theme }) => theme.navLetterSpacing};
  font-size: ${({ theme }) => theme.navFontSize};
  font-weight: ${({ theme }) => theme.navFontWeight};

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: ${({ $keepOrgNameVisible }) => ($keepOrgNameVisible ? 'block' : 'none')};
  }
`;

const BrandAnchor = styled.button`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  margin: 0;
  border: 0;
  padding: 0 15px 0 0;
  height: ${NAVBAR_HEIGHT};
  background: transparent;
  text-align: left;
  font-size: ${Type.Scale._1};
  font-weight: ${Type.Weight.bold};
  color: inherit;
  text-decoration: none;
  min-width: 0;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }

  svg {
    width: auto;
    max-width: 100%;
    height: auto;
    flex-shrink: 0;
  }

  @media (min-width: ${Breakpoints.screen_sm}) {
    flex: 0 0 auto;
  }
`;

const BrandImage = styled.img`
  max-width: 100%;
  height: ${({ theme }) => theme.brandImageHeight || 'auto'};
  width: ${({ theme }) => theme.brandImageWidth || Grid._7};
  flex-shrink: 0;
`;

const OrgLogoImage = styled.img`
  max-width: 100%;
  height: ${({ theme }) => theme.orgLogoHeight || 'auto'};
  width: ${({ theme }) => theme.orgLogoWidth || Grid._7};
  flex-shrink: 0;
`;

function orgNameInBrand(orgName: string | undefined, keepOrgNameVisible?: boolean): React.ReactNode {
  if (!orgName) return null;
  return <OrgName $keepOrgNameVisible={keepOrgNameVisible}>{limitText(orgName, 45)}</OrgName>;
}

const wrap = (inner: React.ReactNode, orgName?: string, keepOrgNameVisible?: boolean) => (
  <BrandAnchor type="button" onClick={() => windowService.redirectTo('/dashboard')}>
    {inner}
    {orgNameInBrand(orgName, keepOrgNameVisible)}
  </BrandAnchor>
);

type BrandMarkProps = {
  orgLogoUrl?: string;
  loggedIn: boolean;
  orgName?: string;
  keepOrgNameVisible?: boolean; // For users with no permissions, we want to keep the org name visible on the brand mark.
};

export function BrandMark({ orgLogoUrl, loggedIn, orgName, keepOrgNameVisible }: BrandMarkProps) {
  const navbarTheme = useNavbarTheme();

  if (orgLogoUrl) {
    return wrap(loggedIn ? <OrgLogoImage src={orgLogoUrl} alt="" /> : null, orgName, keepOrgNameVisible);
  }

  return wrap(
    <BrandImage
      src={navbarTheme.brandImageSrc || 'img/mgpro_logo_sm.svg'}
      title={navbarTheme.brandImageTitle}
      alt={navbarTheme.brandImageAlt || 'Ministry Grid'}
    />,
    orgName,
    keepOrgNameVisible
  );
}
